import CloseButton from '@/components/UI/Buttons/CloseButton';
import React from 'react';
import Logo from '@/components/shared/logos/Logo';
import { NavLink } from 'react-router-dom';
import {
  ArrowRightStartOnRectangleIcon,
  CreditCardIcon,
  GiftIcon,
  HomeIcon,
  QuestionMarkCircleIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { isEmpty, isRegulatedLocale } from '@/shared/utility';
import NotificationBadge from '@/components/UI/NotificationBadge';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import GameIcon from '@/components/UI/Icons/GameIcon';
import { selectIsAuthenticated } from '@/store/selectors/authSelectors';
import { GTagEvents, OpenedFrom, triggerGTag } from '@/lib/gTagManager';
import { useCashierShow } from '@/features/cashier/useCashier';
import { useRewardsWheel } from '@/hooks/useRewardsWheel';
import { cdnImage } from '@/utils/image-utils';
import { selectUser } from '@/store/selectors/userSelectors';
import { selectUiIsSingleGame } from '@/store/ui/selectors';
import StorageManager from '@/shared/storageManager';
import { useLogin } from '@/hooks/useLogin';
import { useRegistration } from '@/hooks/useRegistration';
import { Button } from '@/components/buttons/Button';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { selectPromotionsHasAny } from '@/store/cms/promotions/selectors';
import MobileAppDownload from '@/components/shared/app-download/MobileAppDownload';
import { selectAvailableBonuses } from '@/store/bonuses/selectors';
import { MODALS } from '@/features/modal/types';
import { useModal } from '@/features/modal/useModal';
import { useAbsoluteNavigate } from '@/hooks/useAbsoluteNavigate';
import TotalBalance from '@/components/TotalBalance';
import MenuDrawerNavLinkElement, {
  MenuDrawerNavLinkElementProps,
} from '@/features/drawers/menu/MenuDrawerNavLinkElement';
import { useRestrictedMode } from '@/hooks/useRestrictedMode';

const MenuDrawerUser = React.lazy(() => import('./MenuDrawerUser'));

function MenuDrawer({ closeDrawer }: { closeDrawer: () => void }): JSX.Element {
  const intl = useIntl();
  const user = useSelector(selectUser);
  const locale = useSelector(selectLocale);
  const availableBonuses = useSelector(selectAvailableBonuses);
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const hasPromotions = useSelector(selectPromotionsHasAny);
  const isReturningUser = Boolean(StorageManager.getIsReturningUser());
  const isSingleGame = useSelector(selectUiIsSingleGame);
  const { openModal } = useModal();
  const navigate = useAbsoluteNavigate();

  const { isEnabled: isRewardReelEnabled, pagePath: rewardsWheelPagePath } = useRewardsWheel();
  const { openLogin } = useLogin();
  const { openRegistration } = useRegistration();
  const showCashier = useCashierShow();
  const isRestricted = useRestrictedMode();

  const handleOpenCashier = () => {
    closeDrawer();
    showCashier();
  };

  const handleOpenProfile = () => {
    closeDrawer();
    navigate('/settings');
  };

  const handleAuthentication = () => {
    closeDrawer();
    if (isReturningUser) {
      openLogin({ openedFrom: OpenedFrom.MenuDrawer });
      return;
    }
    openRegistration({ openedFrom: OpenedFrom.MenuDrawer });
  };

  const navLinkElementProps: MenuDrawerNavLinkElementProps[] = [
    {
      to: '/',
      onClick: closeDrawer,
      end: true,
      shouldShow: true,
      children: (
        <>
          <HomeIcon width={30} height={30} />
          <div className="relative pr-1 pt-0.5">{intl.formatMessage({ id: 'header.home' })}</div>
        </>
      ),
    },
    {
      to: 'games',
      onClick: closeDrawer,
      shouldShow: true,
      children: (
        <>
          <GameIcon width={30} height={30} />
          {intl.formatMessage({ id: 'header.games' })}
        </>
      ),
    },
    {
      to: rewardsWheelPagePath,
      onClick: () => {
        triggerGTag(GTagEvents.open_reward_reel, {
          openedFrom: 'menu-drawer',
        });
        closeDrawer();
      },
      shouldShow: isRewardReelEnabled,
      children: (
        <>
          <img
            height="30"
            width="30"
            className="h-[30px] w-[30px]"
            src={cdnImage('rewards-wheel.png', {
              width: '30',
              height: '30',
              dpr: '2',
            })}
            alt={intl.formatMessage({ id: 'rewardReel.title' })}
          />
          {intl.formatMessage({
            id: 'rewardReel.title.cta',
          })}
        </>
      ),
    },
    {
      shouldShow:
        isAuthenticated &&
        availableBonuses.length > 0 &&
        !isRegulatedLocale(locale) &&
        !isRestricted,
      onClick: () => {
        closeDrawer();
        openModal(MODALS.AVAILABLE_BONUSES, { bonuses: availableBonuses });
      },
      children: (
        <>
          <GiftIcon width={30} height={30} />
          <div className="relative pr-1 pt-0.5">
            {intl.formatMessage({ id: 'misc.bonuses' })}
            <NotificationBadge count={availableBonuses.length} />
          </div>
        </>
      ),
    },
    {
      to: 'promotions',
      onClick: closeDrawer,
      children: (
        <>
          <StarIcon width={30} height={30} />
          {intl.formatMessage({ id: 'promotions.header' })}
        </>
      ),
      // Promotions are unavailable in restricted mode — hide the entry point (the page
      // still renders PromotionsRestrictedNotice as a safety net for direct URLs).
      shouldShow: hasPromotions && !isRestricted,
    },
    {
      to: 'help',
      onClick: closeDrawer,
      children: (
        <>
          <QuestionMarkCircleIcon width={30} height={30} />
          {intl.formatMessage({ id: 'header.help' })}
        </>
      ),
      shouldShow: locale !== 'eu',
    },
  ];

  const separatedMenuDrawerNavLinkElementProps: MenuDrawerNavLinkElementProps[] = [
    {
      onClick: handleOpenCashier,
      children: (
        <>
          <CreditCardIcon width={30} height={30} />
          {/* Game page: balance is stale, show a static label instead. */}
          {isSingleGame ? intl.formatMessage({ id: 'header.cashier' }) : <TotalBalance />}
        </>
      ),
      shouldShow: isAuthenticated,
    },
    {
      to: 'logout',
      onClick: closeDrawer,
      children: (
        <>
          <ArrowRightStartOnRectangleIcon width={30} height={30} />
          {intl.formatMessage({ id: 'header.logout' })}
        </>
      ),
      shouldShow: isAuthenticated,
    },
  ];

  return (
    <div className="bg-offWhite flex flex-col h-full pt-[14px] px-[16px]">
      <div className="flex w-full justify-end relative">
        <CloseButton right onClick={closeDrawer} />
      </div>
      <div className="mt-[44px] mx-auto" onClick={closeDrawer}>
        <Logo />
      </div>
      <div className="flex h-full flex-col justify-between">
        <div className="flex flex-col px-[16px] mt-6 gap-10">
          <div className="flex flex-col gap-y-2">
            {navLinkElementProps.map(
              ({ children, to, end, shouldShow, onClick }) =>
                shouldShow && (
                  <MenuDrawerNavLinkElement
                    to={to}
                    onClick={onClick}
                    end={end}
                    key={`menu-nav-element-${to}-${shouldShow}-${end}`}
                  >
                    {children}
                  </MenuDrawerNavLinkElement>
                ),
            )}
          </div>
          {separatedMenuDrawerNavLinkElementProps.map(
            ({ children, to, end, shouldShow, onClick }) =>
              shouldShow && (
                <MenuDrawerNavLinkElement
                  to={to}
                  onClick={onClick}
                  end={end}
                  key={`menu-nav-element-${to}-${shouldShow}-${end}`}
                >
                  {children}
                </MenuDrawerNavLinkElement>
              ),
          )}
        </div>
        <div>
          <div className="flex justify-center mb-3">
            <MobileAppDownload />
          </div>
          {isAuthenticated && user && !isEmpty(user) && (
            <MenuDrawerUser user={user} clickHandler={handleOpenProfile} />
          )}
          {!isAuthenticated && (
            <div className="flex flex-col gap-y-[18px] mb-3">
              {/* Registration is removed in restricted mode — only keep the CTA when it
                  resolves to login (returning user). */}
              {(!isRestricted || isReturningUser) && (
                <Button
                  onClickHandler={handleAuthentication}
                  text={intl.formatMessage({
                    id: isReturningUser ? 'header.login' : 'header.register',
                  })}
                />
              )}
              <div className="flex gap-x-2 justify-between">
                <NavLink className="text-sm" to="terms-and-conditions" onClick={closeDrawer}>
                  {intl.formatMessage({ id: 'footer.termsLinkText' })}
                </NavLink>
                <NavLink className="text-sm" to="privacy-policy" onClick={closeDrawer}>
                  {intl.formatMessage({ id: 'footer.privacyPolicyLinkText' })}
                </NavLink>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default MenuDrawer;
